import React, { useCallback, useEffect, useState } from 'react';
import {
  adminApi,
  SlipListResponse,
  AdminOrderSalesResponse,
  AdminOrderSalesWindow,
  TopProductRow,
} from '../services/api';
import { slipPaymentStatusColor } from '../lib/statusLabels';
import { DashboardCharts } from './DashboardCharts';
import { normalizeSalesByGroup } from '../lib/salesGroups';

interface Props {
  onGoSlips: () => void;
  onGoPromo: () => void;
  onGoStore: () => void;
}

function totalOf(r: SlipListResponse | null): number {
  if (!r) return 0;
  return Number(r.total) || 0;
}

function normSalesWin(w: AdminOrderSalesWindow | undefined): AdminOrderSalesWindow {
  if (!w)
    return {
      order_count: 0,
      cups: 0,
      unique_users: 0,
      by_group: normalizeSalesByGroup(),
      top_products: [],
    };
  const rawTop = w.top_products;
  const top_products: TopProductRow[] = Array.isArray(rawTop)
    ? rawTop.map((r) => ({
        name: typeof r.name === 'string' ? r.name : String((r as { name?: string }).name ?? ''),
        qty: Number((r as { qty?: number }).qty) || 0,
      }))
    : [];
  return {
    order_count: Number(w.order_count) || 0,
    cups: Number(w.cups) || 0,
    unique_users: Number(w.unique_users) || 0,
    by_group: normalizeSalesByGroup(w.by_group as Record<string, number | string> | undefined),
    top_products,
  };
}

type KpiProps = {
  label: string;
  value: number;
  color: string;
  loading: boolean;
  subtitle?: string;
};

const Kpi: React.FC<KpiProps> = ({ label, value, color, loading, subtitle }) => (
  <article className="dash-kpi" style={{ ['--dash-kpi' as string]: color }}>
    <p className="dash-kpi-label">{label}</p>
    <p className="dash-kpi-value" style={{ color }}>
      {loading ? '—' : value.toLocaleString('th-TH')}
    </p>
    {subtitle ? <p className="dash-kpi-sub">{subtitle}</p> : null}
  </article>
);

const DashboardPage: React.FC<Props> = ({ onGoSlips, onGoPromo, onGoStore }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [queuePending, setQueuePending] = useState(0);
  const [expiredNoSlip, setExpiredNoSlip] = useState(0);
  const [d1, setD1] = useState({ verified: 0, rejected: 0, submitted: 0, all: 0 });
  const [d7, setD7] = useState({ verified: 0, rejected: 0, submitted: 0, all: 0 });
  const [sales, setSales] = useState<AdminOrderSalesResponse | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const page1 = { page: 1, limit: 1 } as const;

    try {
      const [q, v1, j1, s1, all1, v7, j7, s7, all7, saleStats] = await Promise.all([
        adminApi.getPending({ ...page1, include_expired_no_slip: true }),
        adminApi.getAll({ ...page1, status: 'verified', since_days: 1 }),
        adminApi.getAll({ ...page1, status: 'rejected', since_days: 1 }),
        adminApi.getAll({ ...page1, status: 'submitted', since_days: 1 }),
        adminApi.getAll({ ...page1, since_days: 1 }),
        adminApi.getAll({ ...page1, status: 'verified', since_days: 7 }),
        adminApi.getAll({ ...page1, status: 'rejected', since_days: 7 }),
        adminApi.getAll({ ...page1, status: 'submitted', since_days: 7 }),
        adminApi.getAll({ ...page1, since_days: 7 }),
        adminApi.getOrderSalesStats(),
      ]);

      setQueuePending(totalOf(q));
      setExpiredNoSlip(Array.isArray(q.expired_orders_no_slip) ? q.expired_orders_no_slip.length : 0);

      setD1({
        verified: totalOf(v1),
        rejected: totalOf(j1),
        submitted: totalOf(s1),
        all: totalOf(all1),
      });
      setD7({
        verified: totalOf(v7),
        rejected: totalOf(j7),
        submitted: totalOf(s7),
        all: totalOf(all7),
      });
      setSales({
        since_1d: normSalesWin(saleStats.since_1d),
        since_7d: normSalesWin(saleStats.since_7d),
        all_time: normSalesWin(saleStats.all_time),
      });
      setLastUpdated(new Date());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const w1 = sales?.since_1d ?? { cups: 0, unique_users: 0, order_count: 0 };
  const w7 = sales?.since_7d ?? { cups: 0, unique_users: 0, order_count: 0 };
  const wAll = sales?.all_time ?? { cups: 0, unique_users: 0, order_count: 0 };
  const best7d: TopProductRow[] = sales?.since_7d?.top_products ?? [];

  return (
    <div className="dash-page">
      <header className="dash-hero">
        <div className="dash-hero-bg" aria-hidden />
        <div className="dash-hero-inner">
          <div className="dash-hero-copy">
            <span className="dash-hero-badge">Coffee Admin</span>
            <h1 className="dash-hero-title">ภาพรวมร้าน</h1>
            <p className="dash-hero-desc">
              แดชบอร์ดสำหรับควบคุมคิวสลิป เทียบช่วงเวลา และเห็นยอดแก้วจากออเดอร์ที่ชำระแล้ว — โชว์ลูกค้าได้ทันทีหลังล็อกอิน
            </p>
            <p className="dash-hero-meta">
              {loading ? 'กำลังซิงก์ข้อมูล…' : lastUpdated ? <>อัปเดตล่าสุด {lastUpdated.toLocaleString('th-TH')}</> : null}
            </p>
          </div>
          <button type="button" className="dash-btn-refresh" disabled={loading} onClick={() => load()}>
            {loading ? 'โหลดอยู่…' : 'รีเฟรชข้อมูล'}
          </button>
        </div>
      </header>

      {error ? <div className="dash-alert">{error}</div> : null}

      <section className="dash-section">
        <h2 className="dash-heading">
          <span className="dash-heading-mark" /> ภาพรวมปฏิบัติการ
        </h2>
        <p className="dash-lead">
          เลขคิวงานเรียลไทม์กับแนวโน้มยอดในช่วงที่ลูกค้าสนใจมากที่สุด (7 วัน)
        </p>
        <div className="dash-kpi-grid">
          <Kpi
            label="สลิปรอตรวจ (คิว)"
            value={queuePending}
            color={slipPaymentStatusColor.submitted}
            loading={loading}
            subtitle="ทั้งระบบ ไม่ตัดช่วงเวลา"
          />
          <Kpi
            label="ออเดอร์หมดเวลาไม่มีสลิป"
            value={expiredNoSlip}
            color="#92400e"
            loading={loading}
            subtitle="จากรายการที่ API แนบ"
          />
          <Kpi label="แก้ว (7 วัน)" value={w7.cups} color="#15803d" loading={loading} subtitle="ออเดอร์ paid" />
          <Kpi label="ลูกค้าที่สั่ง (7 วัน)" value={w7.unique_users} color="#2563eb" loading={loading} subtitle="ไม่ซ้ำ user" />
        </div>
        {(queuePending > 0 || expiredNoSlip > 0) && !loading ? (
          <button type="button" className="dash-cta-queue" onClick={onGoSlips}>
            จัดการคิวสลิปตอนนี้
          </button>
        ) : null}
      </section>

      <section className="dash-section">
        <h2 className="dash-heading">
          <span className="dash-heading-mark" /> กราฟวิเคราะห์
        </h2>
        <p className="dash-lead">จำนวนแก้วตามกลุ่มเมนู (กาแฟ / ชา / น้ำหวาน / ขนม) — จากออเดอร์ชำระแล้วใน 7 วันล่าสุด</p>
        <DashboardCharts loading={loading} salesByGroup7d={sales?.since_7d?.by_group} />
      </section>

      <section className="dash-section">
        <h2 className="dash-heading">
          <span className="dash-heading-mark" /> รายการขายดี
        </h2>
        <p className="dash-lead">
          เมนูยอดนิยมจากออเดอร์ที่ <strong>ชำระแล้ว</strong> — ช่วง 7 วันล่าสุด (เรียงตามจำนวนแก้ว)
        </p>
        {loading ? (
          <div className="dash-bestsellers-skeleton" aria-hidden />
        ) : best7d.length === 0 ? (
          <p className="dash-chart-empty dash-bestsellers-empty">ยังไม่มีข้อมูลขายในช่วงนี้</p>
        ) : (
          <ol className="dash-bestsellers">
            {best7d.map((row, idx) => (
              <li key={`${row.name}-${idx}`} className="dash-bestsellers-item">
                <span className="dash-bestsellers-rank">{idx + 1}</span>
                <span className="dash-bestsellers-name">{row.name}</span>
                <span className="dash-bestsellers-qty">
                  <strong>{row.qty.toLocaleString('th-TH')}</strong>
                  <span className="dash-bestsellers-unit"> แก้ว</span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="dash-section">
        <h2 className="dash-heading">
          <span className="dash-heading-mark" /> ยอดขายรายช่วง
        </h2>
        <div className="dash-sales-row">
          {(
            [
              ['24 ชั่วโมง', w1],
              ['7 วัน', w7],
              ['สะสมทั้งหมด', wAll],
            ] as const
          ).map(([title, w]) => (
            <div key={title} className="dash-sales-card">
              <p className="dash-sales-card-title">{title}</p>
              <dl className="dash-sales-dl">
                <div>
                  <dt>แก้ว</dt>
                  <dd>{loading ? '—' : w.cups.toLocaleString('th-TH')}</dd>
                </div>
                <div>
                  <dt>ลูกค้า</dt>
                  <dd>{loading ? '—' : w.unique_users.toLocaleString('th-TH')}</dd>
                </div>
                <div>
                  <dt>ออเดอร์</dt>
                  <dd>{loading ? '—' : w.order_count.toLocaleString('th-TH')}</dd>
                </div>
              </dl>
            </div>
          ))}
        </div>
        <p className="dash-footnote">
          ยอดช่วงซ้ายนับจาก <strong>ออเดอร์ paid</strong> — การแบ่งประเภทในกราฟอิงจากชื่อเมนูที่บันทึกในออเดอร์ (ตรวจสอบให้ตรงกับเมนูในมินิแอพ) เพิ่มเมนูใหม่แล้วถ้ากราฟไม่ตรง ให้ขยายการแมปที่ API
        </p>
      </section>

      <section className="dash-section">
        <h2 className="dash-heading">
          <span className="dash-heading-mark" /> สลิปย้อนหลัง (จากเวลาอัปโหลด)
        </h2>
        <div className="dash-table-cards">
          <div className="dash-table-card">
            <h3 className="dash-table-card-h">24 ชม.</h3>
            <ul className="dash-metric-list">
              <li>
                <span>อนุมัติ</span>
                <strong style={{ color: slipPaymentStatusColor.verified }}>
                  {loading ? '—' : d1.verified.toLocaleString('th-TH')}
                </strong>
              </li>
              <li>
                <span>ปฏิเสธ</span>
                <strong style={{ color: slipPaymentStatusColor.rejected }}>
                  {loading ? '—' : d1.rejected.toLocaleString('th-TH')}
                </strong>
              </li>
              <li>
                <span>รอตรวจ</span>
                <strong style={{ color: slipPaymentStatusColor.submitted }}>
                  {loading ? '—' : d1.submitted.toLocaleString('th-TH')}
                </strong>
              </li>
              <li>
                <span>ทั้งหมด</span>
                <strong>{loading ? '—' : d1.all.toLocaleString('th-TH')}</strong>
              </li>
            </ul>
          </div>
          <div className="dash-table-card">
            <h3 className="dash-table-card-h">7 วัน</h3>
            <ul className="dash-metric-list">
              <li>
                <span>อนุมัติ</span>
                <strong style={{ color: slipPaymentStatusColor.verified }}>
                  {loading ? '—' : d7.verified.toLocaleString('th-TH')}
                </strong>
              </li>
              <li>
                <span>ปฏิเสธ</span>
                <strong style={{ color: slipPaymentStatusColor.rejected }}>
                  {loading ? '—' : d7.rejected.toLocaleString('th-TH')}
                </strong>
              </li>
              <li>
                <span>รอตรวจ</span>
                <strong style={{ color: slipPaymentStatusColor.submitted }}>
                  {loading ? '—' : d7.submitted.toLocaleString('th-TH')}
                </strong>
              </li>
              <li>
                <span>ทั้งหมด</span>
                <strong>{loading ? '—' : d7.all.toLocaleString('th-TH')}</strong>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <footer className="dash-footer">
        <p className="dash-footer-label">ทางลัด</p>
        <div className="dash-pills">
          <button type="button" className="dash-pill" onClick={onGoStore}>
            ร้าน เปิด/ปิด
          </button>
          <button type="button" className="dash-pill dash-pill--dark" onClick={onGoSlips}>
            จัดการสลิป
          </button>
          <button type="button" className="dash-pill" onClick={onGoPromo}>
            โปรโมชัน
          </button>
        </div>
        <p className="dash-footnote dash-footnote--small">
          ช่วงเวลาสลิปอ้างอิง <code>since_days</code> ตามเวลา UTC ของเซิร์ฟเวอร์ — ใช้คู่กับหน้า &quot;จัดการสลิป&quot;
        </p>
      </footer>
    </div>
  );
};

export default DashboardPage;
